import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import LaTeXRenderer from '../LaTeXRenderer';
import SubmissionGrader from '../shared/SubmissionGrader';
import ScoreMatrix from '../shared/ScoreMatrix';

const GraderDashboard = ({ user: propUser, hasGraderAccess }) => {
  const authUser = JSON.parse(localStorage.getItem('user'));
  const user = propUser || authUser;
  const [tests, setTests] = useState([]);
  const [allSubmissions, setAllSubmissions] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('progress'); // Default to progress tab
  
  // Modal state for detailed problem view
  const [showProblemModal, setShowProblemModal] = useState(false);
  const [modalData, setModalData] = useState({
    student: null,
    test: null,
    problem: null,
    submission: null
  });
  
  // Force update mechanism
  const [updateTrigger, setUpdateTrigger] = useState(0);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      console.log('Fetching dashboard data for grader...');
      
      // Fetch data using grader-accessible endpoints
      const [testsResponse, submissionsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/tests`),
        axios.get(`${API_BASE_URL}/api/submissions`)
      ]);

      console.log('Tests response:', testsResponse.data);
      setTests(testsResponse.data.filter(test => test.status === 'active' || test.status === 'closed'));

      console.log('Submissions response:', submissionsResponse.data);
      const allSubmissionsData = submissionsResponse.data;
      setAllSubmissions(allSubmissionsData);

      // Extract unique students from submissions
      const studentMap = new Map();
      allSubmissionsData.forEach(submission => {
        if (submission.student && submission.student._id) {
          studentMap.set(submission.student._id, submission.student);
        }
      });
      const studentsData = Array.from(studentMap.values());
      setStudents(studentsData);

      console.log('Dashboard data loaded successfully');
    } catch (error) {
      console.error('Detailed error fetching dashboard data:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      setError(`Failed to load dashboard data: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleViewFile = async (submissionId, fileIndex, fileName) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/submissions/${submissionId}/download/${fileIndex}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      } else {
        alert('Failed to load file');
      }
    } catch (error) {
      console.error('File view error:', error);
      alert('Failed to load file');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleDownloadPDF = async (test) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tests/${test._id}/download`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `${test.title.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Failed to download PDF');
      }
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download PDF');
    }
  };

  // Handle opening detailed problem modal
  const handleOpenProblemModal = (studentId, testId, problemNumber) => {
    const student = students.find(s => s._id === studentId);
    const test = tests.find(t => t._id === testId);
    const submission = getSubmission(studentId, testId, problemNumber);
    const problem = test?.problems?.find(p => p.problemNumber === problemNumber);

    setModalData({
      student,
      test,
      problem,
      submission
    });
    setShowProblemModal(true);
  };

  const handleCloseProblemModal = () => {
    setShowProblemModal(false);
    setModalData({
      student: null,
      test: null,
      problem: null,
      submission: null
    });
  };

  // Get submission for specific student, test, and problem
  const getSubmission = (studentId, testId, problemNumber) => {
    // Include updateTrigger to force re-computation when feedback is added
    const submissions = allSubmissions;
    return submissions.find(sub => 
      sub.student._id === studentId && 
      sub.test._id === testId && 
      sub.problemNumber === problemNumber
    );
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
          Loading grader dashboard...
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <h1>📝 Grader Dashboard</h1>
        <h3>Welcome, {user?.name}! 👋</h3>
        <p>Welcome to your grader portal! Thank you for helping grade student submissions.</p>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="card">
        <div style={{ display: 'flex', borderBottom: '2px solid #dee2e6', marginBottom: '20px' }}>
          <button
            onClick={() => setActiveTab('progress')}
            className={`tab-button ${activeTab === 'progress' ? 'active' : ''}`}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'progress' ? '#007bff' : 'transparent',
              color: activeTab === 'progress' ? 'white' : '#007bff',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              marginBottom: '-2px',
              fontWeight: '600'
            }}
          >
            📊 Grading Progress
          </button>
          <button
            onClick={() => setActiveTab('submissions')}
            className={`tab-button ${activeTab === 'submissions' ? 'active' : ''}`}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'submissions' ? '#007bff' : 'transparent',
              color: activeTab === 'submissions' ? 'white' : '#007bff',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              marginBottom: '-2px',
              marginLeft: '8px',
              fontWeight: '600'
            }}
          >
            📋 View All Submissions
          </button>
        </div>

        {/* Grading Progress Tab - Use Shared ScoreMatrix Component */}
        {activeTab === 'progress' && (
          <div>
            <h2>Grading Progress by Test</h2>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Click any problem cell to view submission details and provide feedback. Matrix shows all students and their submission status for each problem.
            </p>
            
            <ScoreMatrix 
              user={user}
              isGraderView={true}
              students={students}
              allSubmissions={allSubmissions}
              tests={tests}
              onOpenProblemModal={handleOpenProblemModal}
              key={updateTrigger} // Force re-render when data updates
            />
          </div>
        )}

        {/* View All Submissions Tab */}
        {activeTab === 'submissions' && (
          <div>
            <h2>All Submissions</h2>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              View all available tests and access bulk grading mode to see all student submissions for comparison and comprehensive grading.
            </p>
            
            {tests.length === 0 ? (
              <div className="text-center" style={{ padding: '40px', color: '#6c757d' }}>
                <h3>No Tests Available</h3>
                <p>No mock tests have been published yet.</p>
              </div>
            ) : (
              <div className="grid grid-2">
                {tests.map((test) => (
                  <div key={test._id} className="card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <h3 style={{ margin: '0', color: '#333' }}>
                        {test.title}
                      </h3>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <span className="badge badge-secondary">
                          Week {test.week}
                        </span>
                        {new Date() > new Date(test.dueDate) && (
                          <span className="badge" style={{ backgroundColor: '#dc3545', color: 'white' }}>
                            Overdue
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {test.description && (
                      <div style={{ color: '#666', marginBottom: '16px' }}>
                        <LaTeXRenderer text={test.description} />
                      </div>
                    )}
                    
                    <div style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>
                      <div><strong>Problems:</strong> {test.problems.length}</div>
                      <div><strong>Due Date:</strong> {formatDate(test.dueDate)}</div>
                      <div><strong>Created by:</strong> {test.createdBy.name}</div>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <Link 
                        to={`/grade/${test._id}`} 
                        className="btn btn-primary"
                        style={{ flex: 1 }}
                      >
                        Grade Submissions
                      </Link>
                      
                      <button
                        onClick={() => handleDownloadPDF(test)}
                        className="btn btn-outline"
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        Download PDF
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Grader Information */}
      {/* <div className="card">
        <h3>Grader Guidelines</h3>
        <div className="grid grid-2">
          <div>
            <h4 style={{ color: '#28a745' }}>✅ What You Can Do:</h4>
            <ul style={{ paddingLeft: '20px', lineHeight: '1.6', color: '#666' }}>
              <li>Grade ungraded submissions</li>
              <li>Provide detailed feedback</li>
              <li>View all test materials</li>
              <li>See existing grades and feedback</li>
              <li>Monitor grading progress</li>
            </ul>
          </div>
          
          <div>
            <h4 style={{ color: '#dc3545' }}>⚠️ Restrictions:</h4>
            <ul style={{ paddingLeft: '20px', lineHeight: '1.6', color: '#666' }}>
              <li>Cannot overwrite existing grades</li>
              <li>Cannot modify admin decisions</li>
              <li>Cannot create or delete tests</li>
              <li>Cannot manage user accounts</li>
              <li>Cannot change test due dates</li>
            </ul>
          </div>
        </div>
      </div> */}

      {/* Problem Detail Modal */}
      {showProblemModal && modalData.student && modalData.test && modalData.problem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            maxWidth: '90%',
            maxHeight: '90%',
            width: '800px',
            backgroundColor: 'white',
            borderRadius: '8px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #dee2e6',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#f8f9fa'
            }}>
              <div>
                <h3 style={{ margin: '0 0 4px 0' }}>
                  Problem {modalData.problem.problemNumber}: {modalData.problem.title}
                </h3>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  <strong>{modalData.student.name}</strong> (ID: {modalData.student.studentId}) • {modalData.test.title}
                </div>
              </div>
              <button
                onClick={handleCloseProblemModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ 
              flex: 1, 
              overflow: 'auto', 
              padding: '20px'
            }}>
              {/* Problem Description */}
              <div className="card" style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 12px 0' }}>Problem Statement</h4>
                {modalData.problem.description && (
                  <div style={{ marginBottom: '16px' }}>
                    <LaTeXRenderer text={modalData.problem.description} />
                  </div>
                )}
                {(modalData.problem.source || modalData.problem.url) && (
                  <div style={{ marginTop: '12px', fontSize: '14px', color: '#555' }}>
                    {modalData.problem.source && (
                      <span><strong>Source:</strong> {modalData.problem.source}</span>
                    )}
                    {modalData.problem.url && (
                      <span style={{ marginLeft: modalData.problem.source ? '16px' : '0' }}>
                        🔗 <a 
                          href={modalData.problem.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: '#007bff', textDecoration: 'underline' }}
                        >
                          Problem Link
                        </a>
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Submission Information */}
              <div className="card">
                <h4 style={{ margin: '0 0 12px 0', color: '#333' }}>Student Submission</h4>
                
                {modalData.submission && (
                  <div style={{ marginBottom: '16px', fontSize: '14px' }}>
                    <div><strong>Submitted:</strong> {formatDate(modalData.submission.submittedAt)}</div>
                    <div><strong>Status:</strong> {modalData.submission.status}</div>
                    {modalData.submission.score !== null && (
                      <div><strong>Score:</strong> {modalData.submission.score} / 7</div>
                    )}
                  </div>
                )}

                {/* Use Shared SubmissionGrader Component */}
                <SubmissionGrader
                  submission={modalData.submission}
                  maxScore={7}
                  onFeedbackUpdated={(updatedSubmission) => {
                    setModalData(prev => ({
                      ...prev,
                      submission: updatedSubmission
                    }));
                    fetchDashboardData(); // Refresh all data
                    setUpdateTrigger(prev => prev + 1); // Force matrix re-render
                  }}
                  onViewFile={handleViewFile}
                  showFiles={true}
                  compact={true}
                />

                {/* Link to Full Grading Interface */}
                {modalData.submission && modalData.submission.status === 'submitted' && (
                  <div style={{ marginTop: '16px' }}>
                    <Link 
                      to={`/grade/${modalData.test._id}?problem=${modalData.problem.problemNumber}&student=${modalData.student._id}`}
                      className="btn btn-outline"
                      style={{ fontSize: '14px' }}
                    >
                      Open Full Grade Interface
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '16px 20px',
              borderTop: '1px solid #dee2e6',
              backgroundColor: '#f8f9fa',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleCloseProblemModal}
                className="btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GraderDashboard;
